import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function TeleRehabPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Remote Rehabilitation Technology
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Our telerehabilitation platform connects patients in remote and underserved areas with expert rehabilitation services through advanced digital technology. This innovative solution ensures that geographical barriers no longer prevent access to quality rehabilitation care.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Combining video consultation, remote monitoring, and digital exercise programs, our tele rehab system provides comprehensive rehabilitation support that is accessible, affordable, and effective for patients wherever they are located.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/c0673455c64a3e9f2e074e84db0d1c19.jpg"
                alt="Tele Rehab System"
                className="w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          <p className="text-gray-600 mb-10 max-w-3xl">
            Comprehensive telerehabilitation platform designed to deliver professional therapy services remotely
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Video Consultation</h3>
              <p className="text-gray-600 leading-relaxed">
                High-quality video calls with therapists for real-time guidance and assessment
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Remote Monitoring</h3>
              <p className="text-gray-600 leading-relaxed">
                Continuous tracking of patient progress and vital signs from any location
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Mobile Accessibility</h3>
              <p className="text-gray-600 leading-relaxed">
                Access rehabilitation programs through smartphones, tablets, or computers
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600 leading-relaxed">
                Book and attend therapy sessions at convenient times without travel
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Digital Exercise Library</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive library of guided exercises with video demonstrations
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Progress Tracking</h3>
              <p className="text-gray-600 leading-relaxed">
                Automated tracking and reporting of rehabilitation outcomes and milestones
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits of Telerehabilitation</h2>
          <p className="text-gray-600 mb-10 max-w-3xl">
            Transforming rehabilitation care delivery through digital innovation
          </p>
          
          <div className="space-y-6 max-w-4xl">
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Accessibility</h3>
              <p className="text-gray-600 leading-relaxed">
                Brings expert rehabilitation services to remote and underserved areas
              </p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cost-Effective</h3>
              <p className="text-gray-600 leading-relaxed">
                Reduces travel costs and time while maintaining quality care
              </p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Continuity of Care</h3>
              <p className="text-gray-600 leading-relaxed">
                Ensures consistent therapy even when in-person visits are not possible
              </p>
            </div>

            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Family Involvement</h3>
              <p className="text-gray-600 leading-relaxed">
                Enables family members to participate in therapy sessions from home
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Clinical Applications</h2>
          <p className="text-gray-600 mb-10 max-w-3xl">
            Versatile telerehabilitation solutions for diverse patient populations and conditions
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            <p>• Post-stroke rehabilitation in rural areas</p>
            <p>• Orthopedic recovery for homebound patients</p>
            <p>• Chronic pain management programs</p>
            <p>• Neurological condition monitoring</p>
            <p>• Pediatric therapy for children in remote locations</p>
            <p>• Geriatric care for elderly patients</p>
            <p>• Post-surgical rehabilitation follow-ups</p>
            <p>• Preventive care and wellness programs</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10">How It Works</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Assessment</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with therapist via video call for comprehensive evaluation
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Plan</h3>
              <p className="text-gray-600 leading-relaxed">
                Receive customized rehabilitation program tailored to your needs
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Remote Sessions</h3>
              <p className="text-gray-600 leading-relaxed">
                Participate in guided therapy sessions from home with real-time feedback
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Monitoring</h3>
              <p className="text-gray-600 leading-relaxed">
                Track improvements and adjust treatment plan based on outcomes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Access Quality Rehabilitation Care From Anywhere
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Don't let distance be a barrier to your recovery. Connect with our expert therapists today.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>Contact Us</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
